export type CheckoutShippingAddress = {
  firstName?: string | null
  lastName?: string | null
  addressLine1?: string | null
  city?: string | null
  zip?: string | null
  state?: {
    name?: string | null
    abbreviation?: string | null
  } | null
  country?: { isoCode?: string | null } | null
}

type SavedCheckoutShippingAddress = CheckoutShippingAddress & {
  dateLastUsed?: string | null
  isDefault?: boolean | null
}

const hasText = (value?: string | null) => Boolean(value?.trim())

export function getMissingCheckoutShippingAddressFields(
  shippingAddress: CheckoutShippingAddress | null | undefined,
  fallbackCountryIsoCode?: string | null
): string[] {
  if (!shippingAddress) {
    return ['shipping address']
  }

  const missing: string[] = []
  const countryIsoCode = (
    shippingAddress.country?.isoCode ??
    fallbackCountryIsoCode ??
    ''
  )
    .trim()
    .toUpperCase()

  if (
    !hasText(shippingAddress.firstName) &&
    !hasText(shippingAddress.lastName)
  ) {
    missing.push('recipient name')
  }
  if (!hasText(shippingAddress.addressLine1)) missing.push('street address')
  if (!hasText(shippingAddress.city)) missing.push('city')
  if (!hasText(shippingAddress.zip)) missing.push('postal code')
  if (!countryIsoCode) missing.push('country')
  if (
    ['CA', 'US'].includes(countryIsoCode) &&
    !hasText(shippingAddress.state?.abbreviation) &&
    !hasText(shippingAddress.state?.name)
  ) {
    missing.push('state / province')
  }

  return missing
}

export function hasCompleteCheckoutShippingAddress(
  shippingAddress: CheckoutShippingAddress | null | undefined,
  fallbackCountryIsoCode?: string | null
): boolean {
  return (
    getMissingCheckoutShippingAddressFields(
      shippingAddress,
      fallbackCountryIsoCode
    ).length === 0
  )
}

export function getPreferredCompleteCheckoutShippingAddress<
  T extends SavedCheckoutShippingAddress,
>(addresses: readonly T[]): T | null {
  const completeAddresses = addresses.filter(address =>
    hasCompleteCheckoutShippingAddress(address)
  )
  const lastUsedCompleteAddress =
    completeAddresses.length > 0
      ? [...completeAddresses]
          .filter(address => Boolean(address.dateLastUsed))
          .sort((a, b) => {
            const dateA = a.dateLastUsed
              ? new Date(a.dateLastUsed).getTime()
              : 0
            const dateB = b.dateLastUsed
              ? new Date(b.dateLastUsed).getTime()
              : 0
            return dateB - dateA
          })[0]
      : null

  return (
    lastUsedCompleteAddress ??
    completeAddresses.find(address => address.isDefault) ??
    null
  )
}
